import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import ResearchLayout from '../components/ResearchLayout';
import api from '../services/api';
import useDashboardPreferences from '../hooks/useDashboardPreferences';

const MedicalAdminDashboard = () => {
  const { user } = useAuth();
  const { preferences } = useDashboardPreferences();
  const [stats, setStats] = useState({
    activeAssignments: 0,
    totalDoctors: 0,
    assignedCases: 0,
    availableProtocols: 0
  });
  const [recentAssignments, setRecentAssignments] = useState([]);
  const [assignedCasesData, setAssignedCasesData] = useState({ cases: [] });
  const [assignmentsData, setAssignmentsData] = useState({ assignments: [] });
  const [batchStats, setBatchStats] = useState({
    totalBatches: 0,
    activeBatches: 0,
    totalBatchCases: 0,
    totalDoctorAssignments: 0
  });

  useEffect(() => {
    if (user) {
      loadDashboardData();
      loadBatchStats();
    }
    // eslint-disable-next-line
  }, [user]);

  const loadDashboardData = async () => {
    try {
      const [assignmentsRes, doctorsRes, protocolsRes] = await Promise.all([
        api.get('/assignments'),
        api.get('/doctors'),
        api.get('/protocols')
      ]);
      
      const assignmentsData = assignmentsRes.data;
      const doctorsData = doctorsRes.data;
      const protocolsData = protocolsRes.data;

      // Enhance assignment data with names
      if (assignmentsData?.assignments) {
        assignmentsData.assignments = assignmentsData.assignments.map(assignment => {
          const doctor = doctorsData?.doctors?.find(d => d.id === assignment.doctor_id);
          const protocol = protocolsData?.protocols?.find(p => p.id === assignment.protocol_id);
          return {
            ...assignment,
            doctor_name: doctor?.name || 'Unknown Doctor',
            protocol_name: protocol?.name || 'Unknown Protocol'
          };
        });
      }
      
      setAssignmentsData(assignmentsData);

      // Filter assignments created by this admin
      const assignments = (assignmentsData.assignments || []).filter(a => a.assigned_by === user.id);
      const totalDoctors = (doctorsData.doctors || []).length;
      const availableProtocols = (protocolsData.protocols || []).length;

      // Calculate unique assigned cases for all assignments created by this admin
      const caseIdSet = new Set();
      assignments.forEach(a => {
        if (a.case_id) caseIdSet.add(a.case_id);
      });
      const assignedCases = caseIdSet.size;

      setStats({
        activeAssignments: assignments.filter(a => a.status === 'active').length,
        totalDoctors,
        assignedCases,
        availableProtocols
      });

      // Group assignments by doctor and protocol to collect all cases
      const grouped = {};
      assignments.forEach(a => {
        const key = `${a.doctor_id}-${a.protocol_id}`;
        if (!grouped[key]) {
          const doctor = (doctorsData.doctors || []).find(u => u.id === a.doctor_id);
          const protocol = (protocolsData.protocols || []).find(p => p.id === a.protocol_id);
          grouped[key] = {
            id: key,
            doctorName: doctor?.name || 'Unknown Doctor',
            protocolName: protocol?.name || 'Unknown Protocol',
            cases: [],
            assignedAt: a.assigned_at,
            status: a.status
          };
        }
        if (a.case_id) grouped[key].cases.push(a.case_id);
        if (new Date(a.assigned_at) > new Date(grouped[key].assignedAt)) {
          grouped[key].assignedAt = a.assigned_at;
        }
      });
      const recentWithDetails = Object.values(grouped)
        .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt))
        .slice(0, 3);

      setRecentAssignments(recentWithDetails);
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
    }
  };

  const loadBatchStats = async () => {
    try {
      const [batchesRes, batchAssignmentsRes] = await Promise.all([
        api.get('/batches'),
        api.get('/batch-assignments/doctor-assignments')
      ]);

      const batches = batchesRes.data.batches || [];
      const batchAssignments = batchAssignmentsRes.data.batches || [];

      // Medical admins and program admins can see ALL batches
      // Doctors only see batches they are assigned to (handled by different endpoint)
      const visibleBatches = (user.role === 'medical_admin' || user.role === 'program_admin') 
        ? batches 
        : batches.filter(batch => batch.created_by === user.id);

      // Calculate statistics
      const now = new Date();
      const activeBatches = visibleBatches.filter(batch => {
        const startDate = new Date(batch.start_date);
        const endDate = new Date(batch.end_date);
        const graceDate = new Date(endDate.getTime() + (batch.grace_period_hours || 12) * 60 * 60 * 1000);
        return now >= startDate && now <= graceDate;
      }).length;

      // Count total cases and doctor assignments for visible batches
      const visibleBatchIds = visibleBatches.map(b => b.id);
      const visibleBatchAssignments = batchAssignments.filter(ba => visibleBatchIds.includes(ba.batch_id));

      // For total batch cases, we need to get case counts from each batch
      let totalBatchCases = 0;
      for (const batch of visibleBatches) {
        try {
          const batchRes = await api.get(`/batches/${batch.id}`);
          if (batchRes.data.cases) {
            totalBatchCases += batchRes.data.cases.length;
          }
        } catch (err) {
          console.warn(`Could not load cases for batch ${batch.id}:`, err);
        }
      }

      // Update batchStats state with visible batches
      setBatchStats({
        totalBatches: visibleBatches.length,
        activeBatches,
        totalBatchCases,
        totalDoctorAssignments: visibleBatchAssignments.length
      });
    } catch (error) {
      console.error('Batch stats fetch error:', error);
      setBatchStats({
        totalBatches: 0,
        activeBatches: 0,
        totalBatchCases: 0,
        totalDoctorAssignments: 0
      });
    }
  };
  
  // Check if role-specific sections should be shown
  const shouldShowSection = (sectionKey) => {
    const roleSpecificSections = preferences.role_specific_sections || {};
    return roleSpecificSections[sectionKey] !== false; // Default to true if not specified
  };

  return (
    <ResearchLayout pageTitle="Medical Admin Dashboard">
      <div className="container-fluid">
        {/* Welcome Section - conditionally rendered based on preferences */}
        {preferences.welcome_section_visible && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm bg-gradient-primary text-white">
                <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                  <h3 className="mb-2">Welcome back, {user?.name}!</h3>
                  <p className="mb-0 opacity-75">
                    Manage protocol assignments, oversee doctors, and coordinate research activities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards - conditionally rendered based on preferences */}
        {preferences.stats_cards_visible && (
          <div className="row mb-4">
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-primary mb-2">
                    <i className="fas fa-tasks fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-primary">{stats.activeAssignments}</h4>
                  <p className="text-muted mb-0">Active Assignments</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-success mb-2">
                    <i className="fas fa-user-md fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-success">{stats.totalDoctors}</h4>
                  <p className="text-muted mb-0">Available Doctors</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-info mb-2">
                    <i className="fas fa-users fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-info">{stats.assignedCases}</h4>
                  <p className="text-muted mb-0">Assigned Cases</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-warning mb-2">
                    <i className="fas fa-clipboard-list fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-warning">{stats.availableProtocols}</h4>
                  <p className="text-muted mb-0">Available Protocols</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Batch Statistics Cards */}
        {preferences.stats_cards_visible && (
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="text-muted mb-3">
                <i className="fas fa-folder-open me-2"></i>
                Batch Management Statistics
              </h6>
            </div>
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-info mb-2">
                    <i className="fas fa-folder fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-info">{batchStats.totalBatches}</h4>
                  <p className="text-muted mb-0">Total Batches Created</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-success mb-2">
                    <i className="fas fa-play-circle fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-success">{batchStats.activeBatches}</h4>
                  <p className="text-muted mb-0">Active Batches</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-primary mb-2">
                    <i className="fas fa-users fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-primary">{batchStats.totalBatchCases}</h4>
                  <p className="text-muted mb-0">Cases in Batches</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-warning mb-2">
                    <i className="fas fa-user-md fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-warning">{batchStats.totalDoctorAssignments}</h4>
                  <p className="text-muted mb-0">Doctor Batch Assignments</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Assignments - role-specific section */}
        {shouldShowSection('recent_activities') && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0">
                    <i className="fas fa-clock text-primary me-2"></i>
                    Recent Protocol Assignments
                  </h5>
                </div>
                <div className="card-body">
                  {recentAssignments.length > 0 ? (
                    <div className="row">
                      {recentAssignments.map((assignment) => (
                        <div key={assignment.id} className="col-lg-4 col-md-6 mb-3">
                          <div className="card border border-primary h-100">
                            <div className="card-header bg-primary text-white">
                              <h6 className="mb-0">
                                <i className="fas fa-user-md me-2"></i>
                                Dr. {assignment.doctorName}
                              </h6>
                            </div>
                            <div className="card-body">
                              <p className="mb-2">
                                <strong>Protocol:</strong> {assignment.protocolName}
                              </p>
                              
                              <div className="mb-3">
                                <strong>Assigned Cases ({assignment.cases.length}):</strong>
                                {assignment.cases.slice(0, 2).map((caseId, idx) => (
                                  <div key={idx} className="mt-1 p-2 bg-light rounded small">
                                    <span>{caseId}</span>
                                  </div>
                                ))}
                                {assignment.cases.length > 2 && (
                                  <div className="small text-muted mt-1">
                                    +{assignment.cases.length - 2} more cases
                                  </div>
                                )}
                              </div>
                              
                              <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                  {new Date(assignment.assignedAt).toLocaleDateString()}
                                </small>
                                <span className="badge bg-success">Active</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <i className="fas fa-tasks fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">No Recent Assignments</h5>
                      <p className="text-muted">Start by assigning protocols to doctors.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending Approvals - role-specific section */}
        {shouldShowSection('pending_approvals') && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-user-md text-success me-2"></i>
                    Doctor Assignment Overview
                  </h5>
                  <button 
                    className="btn btn-sm btn-primary" 
                    onClick={loadDashboardData}
                  >
                    <i className="fas fa-sync-alt me-1"></i> Refresh
                  </button>
                </div>
                <div className="card-body">
                  {assignmentsData?.assignments?.filter(a => 
                    String(a.assigned_by) === String(user?.id) && a.doctor_id)?.length > 0 ? (
                    <>
                      {/* Desktop Table */}
                      <div className="table-responsive d-none d-md-block">
                        <table className="table table-hover align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Doctor</th>
                              <th>Protocol</th>
                              <th>Cases</th>
                              <th>Status</th>
                              <th>Last Updated</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.values(
                              assignmentsData.assignments
                                .filter(a => String(a.assigned_by) === String(user?.id) && a.doctor_id)
                                .reduce((acc, assignment) => {
                                  const key = `${assignment.doctor_id}-${assignment.protocol_id}`;
                                  if (!acc[key]) {
                                    acc[key] = {
                                      doctor_id: assignment.doctor_id,
                                      doctor_name: assignment.doctor_name,
                                      protocol_id: assignment.protocol_id,
                                      protocol_name: assignment.protocol_name,
                                      cases: [],
                                      status: assignment.status,
                                      updated_at: assignment.assigned_at
                                    };
                                  }
                                  acc[key].cases.push(assignment.case_id);
                                  if (new Date(assignment.assigned_at) > new Date(acc[key].updated_at)) {
                                    acc[key].updated_at = assignment.assigned_at;
                                  }
                                  return acc;
                                }, {})
                            ).map((group, idx) => (
                              <tr key={idx}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-success p-2 me-2 d-flex align-items-center justify-content-center" 
                                      style={{ width: '32px', height: '32px' }}>
                                      <i className="fas fa-user-md text-white"></i>
                                    </div>
                                    <div>
                                      <div className="fw-bold">{group.doctor_name}</div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className="badge bg-primary p-2">
                                    {group.protocol_name || 'No Protocol'}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-info text-dark p-2">
                                    <i className="fas fa-clipboard-list me-1"></i>
                                    {group.cases.length} {group.cases.length === 1 ? 'case' : 'cases'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${
                                    group.status === 'active' ? 'bg-success' : 
                                    group.status === 'completed' ? 'bg-secondary' : 'bg-warning'
                                  } p-2`}>
                                    <i className={`fas ${
                                      group.status === 'active' ? 'fa-check-circle' : 
                                      group.status === 'completed' ? 'fa-flag-checkered' : 'fa-clock'
                                    } me-1`}></i>
                                    {group.status}
                                  </span>
                                </td>
                                <td>
                                  <div className="small text-muted">
                                    <i className="fas fa-calendar-alt me-1"></i>
                                    {new Date(group.updated_at).toLocaleDateString()}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Mobile Cards */}
                      <div className="d-block d-md-none">
                        {Object.values(
                          assignmentsData.assignments
                            .filter(a => String(a.assigned_by) === String(user?.id) && a.doctor_id)
                            .reduce((acc, assignment) => {
                              const key = `${assignment.doctor_id}-${assignment.protocol_id}`;
                              if (!acc[key]) {
                                acc[key] = {
                                  doctor_id: assignment.doctor_id,
                                  doctor_name: assignment.doctor_name,
                                  protocol_id: assignment.protocol_id,
                                  protocol_name: assignment.protocol_name,
                                  cases: [],
                                  status: assignment.status,
                                  updated_at: assignment.assigned_at
                                };
                              }
                              acc[key].cases.push(assignment.case_id);
                              if (new Date(assignment.assigned_at) > new Date(acc[key].updated_at)) {
                                acc[key].updated_at = assignment.assigned_at;
                              }
                              return acc;
                            }, {})
                        ).map((group, idx) => (
                          <div key={idx} className="card mb-3 border border-success">
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center mb-2">
                                <div className="rounded-circle bg-success p-2 me-2 d-flex align-items-center justify-content-center" 
                                  style={{ width: '32px', height: '32px' }}>
                                  <i className="fas fa-user-md text-white"></i>
                                </div>
                                <div>
                                  <div className="fw-bold">{group.doctor_name}</div>
                                  <span className="badge bg-primary ms-2">
                                    {group.protocol_name || 'No Protocol'}
                                  </span>
                                </div>
                              </div>
                              <div className="mb-2">
                                <span className="badge bg-info text-dark me-2">
                                  <i className="fas fa-clipboard-list me-1"></i>
                                  {group.cases.length} {group.cases.length === 1 ? 'case' : 'cases'}
                                </span>
                                <span className={`badge ${
                                  group.status === 'active' ? 'bg-success' : 
                                  group.status === 'completed' ? 'bg-secondary' : 'bg-warning'
                                }`}>
                                  <i className={`fas ${
                                    group.status === 'active' ? 'fa-check-circle' : 
                                    group.status === 'completed' ? 'fa-flag-checkered' : 'fa-clock'
                                  } me-1`}></i>
                                  {group.status}
                                </span>
                              </div>
                              <div className="small text-muted">
                                <i className="fas fa-calendar-alt me-1"></i>
                                {new Date(group.updated_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <i className="fas fa-user-md fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">No Doctor Assignments Yet</h5>
                      <p className="text-muted">Assign cases to doctors to see them here.</p>
                      <a href="/medical-admin/assign-cases" className="btn btn-primary">
                        <i className="fas fa-plus-circle me-1"></i>
                        Create New Assignment
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions - conditionally rendered based on preferences */}
        {preferences.quick_actions_visible && (
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0">
                    <i className="fas fa-bolt text-primary me-2"></i>
                    Quick Actions
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/medical-admin/batch" className="btn btn-outline-primary w-100 p-3">
                        <i className="fas fa-folder-open fa-2x mb-2"></i>
                        <br />
                        Manage Batches
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/medical-admin/batch/assign-cases" className="btn btn-outline-secondary w-100 p-3">
                        <i className="fas fa-clipboard-list fa-2x mb-2"></i>
                        <br />
                        Assign Cases to Batches
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/medical-admin/batch/assign-doctors" className="btn btn-outline-warning w-100 p-3">
                        <i className="fas fa-user-md fa-2x mb-2"></i>
                        <br />
                        Assign Batches to Doctors
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/medical-admin/protocols" className="btn btn-outline-info w-100 p-3">
                        <i className="fas fa-clipboard-list fa-2x mb-2"></i>
                        <br />
                        View Protocols
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/medical-admin/time-analytics" className="btn btn-outline-info w-100 p-3">
                        <i className="fas fa-stopwatch fa-2x mb-2"></i>
                        <br />
                        Time Analytics
                      </Link>
                    </div>

                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/medical-admin/quality-of-answers" className="btn btn-outline-primary w-100 p-3">
                        <i className="fas fa-comments fa-2x mb-2"></i>
                        <br />
                        Quality of Answers
                      </Link>
                    </div>

                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/medical-admin/reports" className="btn btn-outline-success w-100 p-3">
                        <i className="fas fa-chart-bar fa-2x mb-2"></i>
                        <br />
                        View Reports
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/medical-admin/assign-cases" className="btn btn-outline-danger w-100 p-3">
                        <i className="fas fa-tasks fa-2x mb-2"></i>
                        <br />
                        Legacy Assignments
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default MedicalAdminDashboard;